import { Box, Divider, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import React, { useState } from 'react';

import {
    PaymentSideBarWrapper,
    autopayInfoBoxSx,
    contractListContainerSx,
    getContractListItemSx,
    noContractFoundSx,
    searchIconSx,
} from '../../pages/dashboard/DashboardStyles';
import { shouldDisablePayNow } from '../../utils/helper';
import { useTranslation } from '../../utils/translations';
import { CustomCheckBox } from '../checkBox/CustomCheckBox';
import { DatePickerValidationReason } from '../date-picker/CommonDatePicker';
import { TextFieldComponent } from '../text-field/text-field-component';
import { colors } from '../theme/theme';
import CommonHeader from '../ui-components/CommonHeader';

import { MobilePaymentContractCard } from './MobilePaymentContractCard';
import { PaymentMethodSelector } from './PaymentMethodSelector';

import type { CardFeeByRowEntry } from './PaymentRowNotices';
import type { BankAccountOption } from '../../hooks/make-payment/useContractPaymentData';
import type { AmountChoice, PaymentMethodType, PaymentRow } from '../../hooks/make-payment/usePaymentRows';
import type { ContractApiResponse } from '../../services/contract-services';
import type { OpenInvoiceApiResponse } from '../../services/payment-services';
import type { SavedPaymentMethodDisplay } from '../modals/AddPaymentMethodModal';
import type { Dispatch, SetStateAction } from 'react';

interface ContractSidebarProps {
    searchTerm: string;
    setSearchTerm: (value: string) => void;
    isLoadingData: boolean;
    activeContracts: ContractApiResponse[];
    canSelectContract: (contractId: string) => boolean;
    isCardUnavailableForContract: (contractId: string) => boolean;
    paymentMethodType: PaymentMethodType;
    paymentRows: PaymentRow[];
    setPaymentRows: Dispatch<SetStateAction<PaymentRow[]>>;
    setSelectedPaymentContracts: Dispatch<SetStateAction<string[]>>;
    userSelectedContractIdsRef: React.MutableRefObject<string[]>;
    buildPaymentRow: (contractId: string) => PaymentRow;
    setPaymentRowErrors: Dispatch<
        SetStateAction<Array<{ bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean }>>
    >;
    setPaymentSubmitErrors: Dispatch<SetStateAction<Array<string | null>>>;
    handleBack: () => void;
    onPaymentMethodChange: (method: PaymentMethodType) => void;
    onAddCardOrAccountClick: () => Promise<void>;
    /** Whether Stripe is enabled (1 = enabled, 0 = disabled) */
    isStripeEnabled?: boolean;
    /** Fully paid and fully covered contracts to hide from sidebar */
    hiddenContractIds?: string[];
    /** Mobile-only: the expandable payment editor rendered inline for each selected contract card. */
    bankAccountOptions: BankAccountOption[];
    savedPaymentMethods: SavedPaymentMethodDisplay[];
    openInvoices: OpenInvoiceApiResponse[];
    resolveContractForPaymentRow: (row: PaymentRow) => ContractApiResponse | null;
    isConsumerLoanContract: (contract: ContractApiResponse | null | undefined) => boolean;
    getEffectivePaymentAmount: (row: PaymentRow, contract: ContractApiResponse | null | undefined) => number;
    cardFeeByRow: CardFeeByRowEntry[];
    cardFeesLoading?: boolean;
    getRowCardFeeAmount: (row: PaymentRow, rowIndex: number) => number;
    paymentRowErrors: Array<{ bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean }>;
    paymentSubmitErrors?: Array<string | null>;
    paymentDatePickerErrors: string[];
    handleBankAccountChange: (index: number, value: string) => void;
    handlePaymentCardChange: (index: number, value: string) => void;
    handleDateChange: (index: number, value: dayjs.Dayjs | null) => void;
    handleDatePickerError: (index: number, reason: DatePickerValidationReason, meta?: { rawInput: string }) => void;
    handleAmountChoiceChange: (index: number, choice: AmountChoice) => void;
    setOtherAmountInputRef: (index: number, el: HTMLInputElement | null) => void;
}

/**
 * The left sidebar: mobile-only header (back button + PaymentMethodSelector) and the contract
 * search + selectable contract list (with Select All and the AutoPay-unavailable divider block).
 */
export const ContractSidebar: React.FC<ContractSidebarProps> = ({
    searchTerm,
    setSearchTerm,
    isLoadingData,
    activeContracts,
    canSelectContract,
    isCardUnavailableForContract,
    paymentMethodType,
    paymentRows,
    setPaymentRows,
    setSelectedPaymentContracts,
    userSelectedContractIdsRef,
    buildPaymentRow,
    setPaymentRowErrors,
    setPaymentSubmitErrors,
    handleBack,
    onPaymentMethodChange,
    onAddCardOrAccountClick,
    isStripeEnabled = false,
    hiddenContractIds = [],
    bankAccountOptions,
    savedPaymentMethods,
    openInvoices,
    resolveContractForPaymentRow,
    isConsumerLoanContract,
    getEffectivePaymentAmount,
    cardFeeByRow,
    cardFeesLoading,
    getRowCardFeeAmount,
    paymentRowErrors,
    paymentSubmitErrors,
    paymentDatePickerErrors,
    handleBankAccountChange,
    handlePaymentCardChange,
    handleDateChange,
    handleDatePickerError,
    handleAmountChoiceChange,
    setOtherAmountInputRef,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [expandedContractId, setExpandedContractId] = useState<string | null>(null);

    const filteredBySearch = activeContracts.filter((contractItem: ContractApiResponse) => {
        const contractId = contractItem.AspireId || `Contract ${contractItem.Id}`;
        // Hide fully paid and fully covered contracts
        if (hiddenContractIds.includes(contractId)) return false;
        return searchTerm === '' || contractId.toLowerCase().includes(searchTerm.toLowerCase());
    });
    const baseSelectableContracts = filteredBySearch.filter((c: ContractApiResponse) =>
        canSelectContract(c.AspireId || `Contract ${c.Id}`),
    );
    const unselectableContracts = filteredBySearch.filter(
        (c: ContractApiResponse) => !canSelectContract(c.AspireId || `Contract ${c.Id}`),
    );
    const selectableContracts =
        paymentMethodType === 'card'
            ? baseSelectableContracts.filter(
                  (c: ContractApiResponse) => !isCardUnavailableForContract(c.AspireId || `Contract ${c.Id}`),
              )
            : baseSelectableContracts;
    const selectableContractIds = selectableContracts.map((c: ContractApiResponse) => c.AspireId || `Contract ${c.Id}`);
    const allSelectableSelected =
        selectableContractIds.length > 0 &&
        selectableContractIds.every((id: string) => paymentRows.some(row => row.contractId === id));

    const renderContractItem = (
        contractItem: ContractApiResponse,
        isSelectable: boolean,
        disableReason?: 'card_fee_unavailable',
    ) => {
        const contractId = contractItem.AspireId || `Contract ${contractItem.Id}`;
        const isSelected = paymentRows.some(row => row.contractId === contractId);
        const canSelect = isSelectable;
        const isDisabled = shouldDisablePayNow(contractId, activeContracts);
        const tooltipMessage = !canSelect
            ? disableReason === 'card_fee_unavailable'
                ? t('makePayment.cardPaymentNotAvailableForLocation')
                : isDisabled
                  ? t('dashboard.payments.upcomingPayments.autopayEnabledTooltip')
                  : `${contractId} has no outstanding balance to pay`
            : '';
        const handleToggleContract = (e?: React.MouseEvent) => {
            if (!canSelect) {
                e?.preventDefault();
                e?.stopPropagation();
                return;
            }
            const isCurrentlySelected = paymentRows.some(row => row.contractId === contractId);
            if (isCurrentlySelected) {
                setPaymentRows(prev => prev.filter(row => row.contractId !== contractId));
                setSelectedPaymentContracts(prev => prev.filter(c => c !== contractId));
                userSelectedContractIdsRef.current = userSelectedContractIdsRef.current.filter(c => c !== contractId);
                setPaymentRowErrors([]);
                // Removing a row shifts every later row's index, so an index-keyed submit error would
                // otherwise end up displayed against the wrong (now-shifted) contract.
                setPaymentSubmitErrors([]);
            } else {
                const newRow = buildPaymentRow(contractId);
                setPaymentRows(prev => [...prev, newRow]);
                setSelectedPaymentContracts(prev => (prev.includes(contractId) ? prev : [...prev, contractId]));
                if (!userSelectedContractIdsRef.current.includes(contractId)) {
                    userSelectedContractIdsRef.current = [...userSelectedContractIdsRef.current, contractId];
                }
            }
        };

        const rowIndex = paymentRows.findIndex(row => row.contractId === contractId);
        const row = rowIndex !== -1 ? paymentRows[rowIndex] : null;
        const isExpanded = expandedContractId === contractId;

        if (!isMobile) {
            // Desktop/tablet: compact checkbox row — selection only, editing happens in the payment-rows table.
            return (
                <Tooltip
                    key={contractId}
                    title={disableReason === 'card_fee_unavailable' ? '' : tooltipMessage}
                    arrow
                    placement="bottom"
                >
                    <Stack
                        sx={{
                            ...getContractListItemSx(isSelectable ? isSelected : false),
                            ...(isSelectable
                                ? paymentMethodType === 'card'
                                    ? { cursor: 'pointer' }
                                    : {}
                                : {
                                      pointerEvents: 'none',
                                      opacity: 0.7,
                                      userSelect: 'none',
                                      ...(paymentMethodType === 'card' ? { cursor: 'not-allowed' } : {}),
                                  }),
                        }}
                        direction="row"
                        alignItems="flex-start"
                        gap={1}
                        onClick={isSelectable ? handleToggleContract : undefined}
                    >
                        <CustomCheckBox
                            disabled={!isSelectable}
                            checked={isSelectable && isSelected}
                            size="small"
                            slotProps={{
                                input: {
                                    'aria-label': `Select contract ${contractId}`,
                                },
                            }}
                            sx={{
                                '& svg': { height: '16px', width: '16px' },
                                mt: 0.25,
                            }}
                        />
                        <Stack sx={{ flex: 1, gap: 0.5, minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                color={isSelectable ? colors.fullBlack : colors.greyPreset[150]}
                            >
                                {contractId}
                            </Typography>
                        </Stack>
                    </Stack>
                </Tooltip>
            );
        }

        // Mobile: checkbox + inline expandable payment editor, replacing the separate chip grid.
        return (
            <Box key={contractId}>
                <MobilePaymentContractCard
                    contractId={contractId}
                    contractData={contractItem}
                    isSelectable={isSelectable}
                    tooltipMessage={disableReason === 'card_fee_unavailable' ? '' : tooltipMessage}
                    row={row}
                    rowIndex={rowIndex !== -1 ? rowIndex : null}
                    isExpanded={isExpanded}
                    onToggleExpand={() => setExpandedContractId(prev => (prev === contractId ? null : contractId))}
                    onToggleSelect={() => handleToggleContract()}
                    paymentMethodType={paymentMethodType}
                    isStripeEnabled={isStripeEnabled}
                    bankAccountOptions={bankAccountOptions}
                    savedPaymentMethods={savedPaymentMethods}
                    openInvoices={openInvoices}
                    resolveContractForPaymentRow={resolveContractForPaymentRow}
                    isConsumerLoanContract={isConsumerLoanContract}
                    getEffectivePaymentAmount={getEffectivePaymentAmount}
                    cardFeeEntry={rowIndex !== -1 ? cardFeeByRow[rowIndex] : undefined}
                    cardFeesLoading={cardFeesLoading}
                    getRowCardFeeAmount={getRowCardFeeAmount}
                    paymentRowError={rowIndex !== -1 ? paymentRowErrors[rowIndex] : undefined}
                    paymentSubmitError={rowIndex !== -1 ? paymentSubmitErrors?.[rowIndex] : undefined}
                    paymentDatePickerError={rowIndex !== -1 ? paymentDatePickerErrors[rowIndex] : undefined}
                    handleBankAccountChange={handleBankAccountChange}
                    handlePaymentCardChange={handlePaymentCardChange}
                    handleDateChange={handleDateChange}
                    handleDatePickerError={handleDatePickerError}
                    handleAmountChoiceChange={handleAmountChoiceChange}
                    setOtherAmountInputRef={setOtherAmountInputRef}
                    setPaymentRows={setPaymentRows}
                    setPaymentRowErrors={setPaymentRowErrors}
                    setPaymentSubmitErrors={setPaymentSubmitErrors}
                />
            </Box>
        );
    };

    return (
        <Stack sx={{ width: { xs: '100%', md: '205px' } }}>
            <Stack direction={'column'} p={2} alignItems={'flex-start'} display={{ xs: 'block', md: 'none' }}>
                <Stack direction={'row'} alignItems={'center'} gap={2}>
                    <CommonHeader
                        icon="icon-arrow-left"
                        cursorPointer="pointer"
                        mainTitle={t('dashboard.makePayment.mainTitle')}
                        subTitle={t('dashboard.makePayment.subTitle')}
                        mainTitleFS={20}
                        subTitleFS={13}
                        onclick={handleBack}
                        iconBgColor={colors.fullWhite}
                        iconColor={colors.primaryLight[100]}
                        borderColor={colors.primaryLight[100]}
                    />
                </Stack>
                {/* Header - Pay via (Card | Bank Account) & Add Card / Add Account */}
                <PaymentMethodSelector
                    variant="mobile"
                    paymentMethodType={paymentMethodType}
                    onPaymentMethodChange={onPaymentMethodChange}
                    onAddCardOrAccountClick={onAddCardOrAccountClick}
                    isStripeEnabled={isStripeEnabled}
                />
            </Stack>
            {/* Contract Search and List */}
            <PaymentSideBarWrapper spacing={1.5} py={{ xs: 0, md: 1.5 }}>
                <Stack sx={{ px: 1.5, gap: 1.5 }}>
                    <Typography variant="h6" color="textSecondary">
                        {t('makePayment.contracts')}
                    </Typography>
                    {/* Search Field */}
                    <TextFieldComponent
                        ariaLabel="Search Contract ID"
                        placeholder="Search Contract ID"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        startIcon={<span className="icon-search" style={searchIconSx(theme) as React.CSSProperties} />}
                    />
                </Stack>

                {/* Available Contracts List */}
                <Box sx={contractListContainerSx}>
                    <Stack spacing={1}>
                        {isLoadingData ? (
                            <Stack spacing={1} px={1.5}>
                                {activeContracts
                                    .slice(0, Math.max(activeContracts.length, 2))
                                    .map((_: ContractApiResponse, i: number) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                height: 32,
                                                borderRadius: 1,
                                                bgcolor: 'action.hover',
                                                animation: 'pulse 1.5s ease-in-out infinite',
                                                '@keyframes pulse': {
                                                    '0%, 100%': { opacity: 1 },
                                                    '50%': { opacity: 0.4 },
                                                },
                                            }}
                                        />
                                    ))}
                            </Stack>
                        ) : (
                            <>
                                {/* Select All - only for selectable contracts */}
                                {selectableContracts.length > 0 && (
                                    <Stack
                                        direction={'row'}
                                        sx={{
                                            cursor: 'pointer',
                                            borderRadius: 2,
                                            px: 1.5,
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                        onClick={() => {
                                            if (allSelectableSelected) {
                                                setPaymentRows(prev =>
                                                    prev.filter(row => !selectableContractIds.includes(row.contractId)),
                                                );
                                                setSelectedPaymentContracts(prev =>
                                                    prev.filter(c => !selectableContractIds.includes(c)),
                                                );
                                                userSelectedContractIdsRef.current =
                                                    userSelectedContractIdsRef.current.filter(
                                                        c => !selectableContractIds.includes(c),
                                                    );
                                                setPaymentRowErrors([]);
                                                setPaymentSubmitErrors([]);
                                            } else {
                                                const toAdd = selectableContractIds.filter(
                                                    (id: string) => !paymentRows.some(row => row.contractId === id),
                                                );
                                                if (toAdd.length === 0) return;
                                                setSelectedPaymentContracts(prev => [...new Set([...prev, ...toAdd])]);
                                                userSelectedContractIdsRef.current = [
                                                    ...new Set([...userSelectedContractIdsRef.current, ...toAdd]),
                                                ];
                                                const newRows: PaymentRow[] = toAdd.map(buildPaymentRow);
                                                setPaymentRows(prev => [...prev, ...newRows]);
                                            }
                                        }}
                                    >
                                        <CustomCheckBox
                                            checked={allSelectableSelected}
                                            size="small"
                                            slotProps={{
                                                input: {
                                                    'aria-label': t('makePayment.selectAll') || 'Select all contracts',
                                                },
                                            }}
                                            sx={{
                                                '& svg': { height: '16px', width: '16px' },
                                                mt: 0.25,
                                            }}
                                        />
                                        <Typography variant="body2" color="textPrimary">
                                            {t('makePayment.selectAll')}
                                        </Typography>
                                    </Stack>
                                )}
                                {/* Selectable contracts */}
                                <Stack direction={'column'}>
                                    <Stack className="contractListContainer">
                                        {selectableContracts.map((contractItem: ContractApiResponse) =>
                                            renderContractItem(contractItem, true),
                                        )}
                                    </Stack>

                                    {/* Separate unselectable contracts by reason */}
                                    {unselectableContracts.length > 0 && (
                                        <Stack>
                                            {(() => {
                                                const autopayDisabledContracts = unselectableContracts.filter(c => {
                                                    const contractId = c.AspireId || `Contract ${c.Id}`;
                                                    return shouldDisablePayNow(contractId, activeContracts);
                                                });
                                                const noInvoicesContracts = unselectableContracts.filter(c => {
                                                    const contractId = c.AspireId || `Contract ${c.Id}`;
                                                    return !shouldDisablePayNow(contractId, activeContracts);
                                                });

                                                return (
                                                    <>
                                                        {/* AutoPay Enabled Contracts Section */}
                                                        {autopayDisabledContracts.length > 0 && (
                                                            <Stack>
                                                                <Divider sx={{ mx: { xs: 0, md: 2 }, mt: 1 }} />
                                                                <Box sx={autopayInfoBoxSx}>
                                                                    <span
                                                                        className="icon-info-circle"
                                                                        style={{ fontSize: 16 }}
                                                                    />
                                                                    <Typography
                                                                        variant="caption"
                                                                        fontWeight={500}
                                                                        sx={{ textTransform: 'none' }}
                                                                    >
                                                                        {t('makePayment.autopayContractsNotAvailable')}
                                                                    </Typography>
                                                                </Box>
                                                                <Stack className="contractListContainer">
                                                                    {autopayDisabledContracts.map(
                                                                        (contractItem: ContractApiResponse) =>
                                                                            renderContractItem(contractItem, false),
                                                                    )}
                                                                </Stack>
                                                            </Stack>
                                                        )}
                                                        {/* No Open Invoices Contracts Section */}
                                                        {noInvoicesContracts.length > 0 && (
                                                            <Stack>
                                                                <Divider sx={{ mx: { xs: 0, md: 2 }, mt: 1 }} />
                                                                <Box sx={autopayInfoBoxSx}>
                                                                    <span
                                                                        className="icon-info-circle"
                                                                        style={{ fontSize: 16 }}
                                                                    />
                                                                    <Typography
                                                                        variant="caption"
                                                                        fontWeight={500}
                                                                        sx={{ textTransform: 'none' }}
                                                                    >
                                                                        {t(
                                                                            'makePayment.noInvoicesContractsNotAvailable',
                                                                        )}
                                                                    </Typography>
                                                                </Box>
                                                                <Stack className="contractListContainer">
                                                                    {noInvoicesContracts.map(
                                                                        (contractItem: ContractApiResponse) =>
                                                                            renderContractItem(contractItem, false),
                                                                    )}
                                                                </Stack>
                                                            </Stack>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </Stack>
                                    )}
                                </Stack>
                            </>
                        )}
                        {!isLoadingData &&
                            searchTerm !== '' &&
                            activeContracts.filter((contractItem: ContractApiResponse) => {
                                const contractId = contractItem.AspireId || `Contract ${contractItem.Id}`;
                                return contractId.toLowerCase().includes(searchTerm.toLowerCase());
                            }).length === 0 && (
                                <Typography variant="body2" color="textSecondary" sx={noContractFoundSx}>
                                    {'No contracts found'}
                                </Typography>
                            )}
                        {activeContracts.length === 0 && (
                            <Typography variant="caption" color="textSecondary">
                                No available contracts
                            </Typography>
                        )}
                    </Stack>
                </Box>
            </PaymentSideBarWrapper>
        </Stack>
    );
};
